import dealService from 'resources/deal/deal.service';
import { DEAL_STATUSES } from 'resources/deal/deal.constants';
import kitService from 'resources/listedkit/listedkit.service';
import dealEventService from 'resources/deal-event/deal-event.service';
import { LISTEDKIT_TYPES } from 'resources/listedkit/listedkit.constants';
import documentService from 'resources/document/document.service';
import { deleteDocument } from 'resources/document/workflows/deleteDocument';
import milestoneService from 'resources/milestone/milestone.service';
import { MILESTONE_TYPES } from 'resources/milestone/milestone.constants';
import { deleteMilestone } from 'resources/milestone/workflow';
import { DEAL_EVENT_TYPES } from 'resources/deal-event/deal-event.constants';
import auditService from 'resources/audit/audit.service';
/**
 * Reverts a transaction to Active Listing state
 * @param {string} dealId - The ID of the transaction to revert
 * @param {string} userId - The ID of the user who is reverting the transaction to Active Listing
 * @param {string} preContractTemplateKitId - The ID of the Pre-Contract template kit to use (`null` when the transaction was in Active Listing before Under Contract)
 * @returns {Promise<void>}
 */
export default async function revertToActiveListing(dealId, userId) {
  const deal = await dealService.findById(dealId);

  // step 1: Get transaction's Under Contract kit
  const underContractKit = await kitService.getListedKitByTypeAndDealId({
    dealId,
    type: LISTEDKIT_TYPES.SELLER_UNDER_CONTRACT,
  });

  // step 2: Find all documents created when the transaction was in Under Contract state
  const underContractDocuments = await getUnderContractDocuments(deal, underContractKit);
  const underContractDocumentIds = underContractDocuments.map((document) => document.id);

  // step 3: Remove all Documents created when the transaction was in Under Contract state
  if (underContractDocumentIds.length) {
    await Promise.all(underContractDocumentIds.map((documentId) => deleteDocument(documentId)));
  }

  // step 4: Remove the Under Contract kit
  if (underContractKit) {
    await kitService.removeListedKit(underContractKit.id);
  }

  // step 5: Remove all Milestones (with their Tasks) and Audits (aka Reviews) created when the transaction was in Under Contract state
  const dealMilestones = await milestoneService.getMilestonesByDealId(dealId) || [];
  const milestonesToDelete = dealMilestones.filter((milestone) => milestone.type !== MILESTONE_TYPES.PRE_CONTRACT);
  const auditsToDelete = await auditService.getUnderContractAuditsByDealId(dealId) || [];
  await Promise.all([
    ...milestonesToDelete.map((milestone) => deleteMilestone(milestone.id)),
    ...auditsToDelete.map((audit) => auditService.removeAudit(audit.id)),
  ]);

  // step 6: Set the Transaction to Active Listing state
  // and leave only the documents that were created when the transaction was in Active Listing state (if any)
  const response = await dealService.updateOne({ id: deal.id }, {
    status: DEAL_STATUSES.ACTIVE_LISTING,
    // leave only `document_ids` and filter out the ones that were deleted
    transaction: {
      document_ids: (deal.transaction.document_ids.length && underContractDocumentIds.length)
        ? deal.transaction.document_ids.filter((documentId) => !underContractDocumentIds.includes(documentId))
        : [],
    },
  });

  // step 7: Create a deal event for the revert to Active Listing
  if (userId) {
    try {
      await dealEventService.createDealEvent({
        type: DEAL_EVENT_TYPES.REVERTED_TO_ACTIVE_LISTING,
        dealId: deal.id,
        companyId: deal.company_id,
        createdBy: userId,
      });
    } catch (error) {
      // log the error but don't throw it, because it's not a blocker for the transaction to be reverted to Active Listing
      console.error('Error creating deal event for revert to Active Listing', error);
    }
  }

  return response;
};

const getUnderContractDocuments = async (deal, underContractKit) => {
  const dealDocumentIds = deal.transaction?.document_ids || [];
  if (!dealDocumentIds.length) return [];

  const dealDocuments = await documentService.findByIds(dealDocumentIds) || [];
  if (!dealDocuments.length) return [];

  const underContractDateTime = await getUnderContractDateTime(deal, underContractKit);
  if (!underContractDateTime) {
    throw new Error('Error: cannot clean up documents created when the transaction was in Under Contract state.');
  }

  // filter the documents created after Under Contract kit was created
  const underContractDocuments = dealDocuments.filter((document) => document.created_on >= underContractDateTime)
  return underContractDocuments;
};

const getUnderContractDateTime = async (deal, underContractKit) => {
  if (underContractKit) {
    return underContractKit.created_on;
  }

  // if the Under Contract kit doesn't exist, get the latest event of type `IS_UNDER_CONTRACT` and use its `created_on` date
  const underContractEvents = await dealEventService.getDealEventsByDealId(
    deal.id,
    DEAL_EVENT_TYPES.IS_UNDER_CONTRACT,
  );
  
  return underContractEvents[0]?.created_on;
}
